import { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingUp } from "lucide-react";
import { ScrollReveal } from "./ScrollReveal";

const niches = ["Fitness", "Business", "Finance", "Food", "Lifestyle", "Tech", "Gaming", "Education", "Beauty", "Travel", "Other"];

const productTypes = [
  { label: "Ebook ($27)", price: 27 },
  { label: "Creator Swipe File ($37)", price: 37 },
  { label: "Mini Course ($47)", price: 47 },
  { label: "Full Course ($97)", price: 97 },
  { label: "Coaching ($197)", price: 197 },
];

const useAnimatedValue = (target: number, duration = 600) => {
  const [value, setValue] = useState(target);
  const prevRef = useRef(target);

  useEffect(() => {
    const from = prevRef.current;
    const diff = target - from;
    if (diff === 0) return;
    let startTime: number;
    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const p = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.floor(from + eased * diff));
      if (p < 1) requestAnimationFrame(animate);
      else prevRef.current = target;
    };
    requestAnimationFrame(animate);
  }, [target, duration]);

  return value;
};

export const CreatorCalculatorSection = () => {
  const [followers, setFollowers] = useState(1000);
  const [niche, setNiche] = useState(niches[0]);
  const [productIndex, setProductIndex] = useState(2);
  const [showHalf, setShowHalf] = useState(true);

  const result = useMemo(() => {
    const price = productTypes[productIndex].price;
    const onePercent = Math.floor(followers * 0.01) * price;
    const halfPercent = Math.floor(followers * 0.005) * price;
    return { onePercent, halfPercent, price, productLabel: productTypes[productIndex].label };
  }, [followers, productIndex]);

  const animatedIncome = useAnimatedValue(result.onePercent);

  useEffect(() => {
    setShowHalf(false);
    const t = setTimeout(() => setShowHalf(true), 200);
    return () => clearTimeout(t);
  }, [result.onePercent]);

  const formatFollowers = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
    return n.toString();
  };

  const selectStyle: React.CSSProperties = {
    border: "1px solid #E5E7EB",
    background: "#FFFFFF",
    color: "#111827",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%237C3AED' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    backgroundSize: "16px",
  };

  return (
    <section id="calculator" className="py-10 md:py-20 relative" style={{ background: "#FFFFFF" }}>
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <ScrollReveal>
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-display font-bold mb-3 md:mb-4" style={{ color: "#111827", fontSize: "clamp(24px, 3.5vw, 40px)", lineHeight: 1.2 }}>
              Find Out What Your Audience Is Worth <span style={{ color: "#F59E0B" }}>Right Now.</span>
            </h2>
            <p className="text-sm md:text-lg max-w-2xl mx-auto" style={{ color: "#4B5563" }}>
              Move the slider to your follower count, choose your niche and product type, and see your estimated monthly income.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="max-w-3xl mx-auto">
            <div className="rounded-2xl p-6 md:p-10 space-y-8 bg-white" style={{ border: "1px solid #E5E7EB", boxShadow: "0 4px 24px rgba(0,0,0,0.04)" }}>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "#111827" }}>
                  My audience size: <span className="font-bold text-lg" style={{ color: "#F59E0B" }}>{formatFollowers(followers)}</span>
                </label>
                <input
                  type="range" min={100} max={500000} step={100} value={followers}
                  onChange={(e) => setFollowers(Number(e.target.value))}
                  className="w-full h-3 rounded-full appearance-none cursor-pointer touch-manipulation"
                  style={{ minHeight: "44px", background: "#E5E7EB", accentColor: "#7C3AED" }}
                />
                <div className="flex justify-between text-xs mt-1" style={{ color: "#9CA3AF" }}>
                  <span>500</span><span>5K</span><span>50K</span><span>500K</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "#111827" }}>My niche</label>
                <select value={niche} onChange={(e) => setNiche(e.target.value)}
                  className="w-full rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED] appearance-none pr-10 input-focus"
                  style={selectStyle}
                >
                  {niches.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: "#111827" }}>Product type</label>
                <select value={productIndex} onChange={(e) => setProductIndex(Number(e.target.value))}
                  className="w-full rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED] appearance-none pr-10 input-focus"
                  style={selectStyle}
                >
                  {productTypes.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
                </select>
              </div>

              <div className="rounded-xl p-6 md:p-8 text-center" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}>
                <p className="text-sm mb-2" style={{ color: "#6B7280" }}>Estimated monthly income</p>
                <p className="font-display text-4xl md:text-6xl font-bold mb-2 flex items-center justify-center gap-3 transition-all duration-300" style={{ color: "#F59E0B" }}>
                  <TrendingUp className="w-6 h-6 md:w-8 md:h-8" />
                  ${animatedIncome.toLocaleString()}
                </p>
                <p className="text-sm md:text-base mb-2" style={{ color: "#4B5563" }}>
                  Based on a 1% conversion rate of your audience buying {result.productLabel} at ${result.price}
                </p>
                <p
                  className="text-sm transition-opacity duration-300"
                  style={{ color: "#6B7280", opacity: showHalf ? 1 : 0 }}
                >
                  Even at 0.5% conversion: <span className="font-semibold" style={{ color: "#F59E0B" }}>${result.halfPercent.toLocaleString()}/month</span>
                </p>
                <p className="text-xs mt-3 italic" style={{ color: "#9CA3AF" }}>
                  These are conservative estimates. Many creators on AI Storycraft Studio exceed these numbers.
                </p>
              </div>

              <div className="flex justify-center">
                <a href="#pricing" className="inline-block w-full md:w-auto">
                  <Button variant="hero" size="xl" className="group text-base md:text-lg px-8 py-5 md:px-12 md:py-6 w-full md:w-auto max-w-[400px] btn-interactive">
                    Start Your Free Trial
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};
